import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { Avatar, Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertDialog, MButton, ProgressLoaders, Tag } from 'components'
import { color } from 'config/color'
import { useAuth } from 'context'
import { APIs } from 'services'
import { isAuthorized, useMobileBreakpoints } from 'utils'
import { CLIENT } from 'config'

export const InvoiceCloud = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''

  const isMobile = useMobileBreakpoints()
  const { authToken, getICStatus, handleLogOut } = useAuth()

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isVerified, setIsVerified] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const isInvoiceCloudEnabled = process.env.REACT_APP_CLIENT_NAME !== CLIENT.WEB

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const verifyICCredentials = async (): Promise<void> => {
    setIsLoading(true)
    try {
      if (email === '' || email === undefined) return

      const result = await getICStatus(email)
      if (result.success !== null || result.userAuth !== null) {
        setIsVerified(false)
      }

      setIsVerified(true)
    } catch (e: any) {
      if (!isAuthorized(e)) await handleLogOut()
      setIsVerified(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSyncPassword = async (): Promise<void> => {
    try {
      setIsLoading(true)
      const result = await APIs.syncPassword({ email }, authToken)
      if (result.success === true) {
        setError('')
        setIsDialogOpen(true)
      }
    } catch (e: any) {
      if (!isAuthorized(e)) await handleLogOut()
      if (e?.response?.data?.response?.includes?.('Please check InvoiceCloud.')) {
        setError(e?.response?.data?.response)
        setIsDialogOpen(true)
        return
      }
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to sync password')
      setIsDialogOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isInvoiceCloudEnabled) return

    void verifyICCredentials()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  if (isLoading) {
    return (
      <Box border={`1px solid ${color.secondary.dark[300]}`} p={2}>
        <ProgressLoaders height="100%" />
      </Box>
    )
  }

  return (
    <>
      {isInvoiceCloudEnabled && (
        <>
          <Box border={`1px solid ${color.secondary.dark[300]}`}>
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              p={1}
              bgcolor={color.grey[50]}
              gap={isMobile ? 1 : 0}
            >
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Avatar
                  sx={{
                    color: color.grey[500],
                    backgroundColor: color.grey[200],
                    height: 26,
                    width: 26,
                    padding: 1
                  }}
                >
                  IC
                </Avatar>

                <Box
                  display="flex"
                  flexDirection={isMobile ? 'column' : 'row'}
                  alignItems="center"
                  gap={1}
                >
                  <Typography color={color.black} fontWeight={600} fontSize={16}>
                    Invoice Cloud
                  </Typography>
                  <Tag
                    type={isVerified ? 'success' : 'error'}
                    message={isVerified ? 'Registered' : 'Unregistered'}
                    Icon={isVerified ? CheckCircleIcon : ErrorIcon}
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} justifyContent="flex-end">
                <MButton
                  variant="outlined"
                  type="button"
                  rounded="true"
                  texttransform="none"
                  fullWidth={isMobile}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleSyncPassword}
                  loading={isLoading}
                  disabled={isLoading || isVerified}
                >
                  Register
                </MButton>
              </Box>
            </Box>

            {/* <PasswordDialog
              type={isRegisterDialogOpen ? 'register' : 'changePassword'}
              open={isRegisterDialogOpen || isDialogOpen}
              onClose={() => {
                setIsDialogOpen(false)
                setIsRegisterDialogOpen(false)
              }}
            /> */}
          </Box>
          <AlertDialog
            open={isDialogOpen}
            type={error !== '' ? 'error' : 'success'}
            title={error !== '' ? 'Error' : 'Success'}
            message={error !== '' ? error : 'Password synced successfully'}
            handleClick={() => { setIsDialogOpen(false) }}
          ></AlertDialog>
        </>
      )}
    </>
  )
}
